import { useEffect, useRef, useState } from 'react';
import { RouteProvider } from './context/RouteContext';
import { AuthProvider } from './context/AuthContext';
import { Sidebar } from './components/Sidebar/Sidebar';
import { MapView } from './components/Map/MapView';
import { AuthBadge, BUSINESS_SIGNUP_INTENT_KEY } from './components/AuthBadge/AuthBadge';
import { ProfilePage } from './components/Profile/ProfilePage';
import { BusinessApplicationModal } from './components/Profile/BusinessApplicationModal';
import { BusinessDashboard } from './components/Dashboard/BusinessDashboard';
import { AdminPanel } from './components/Admin/AdminPanel';
import { MyRidesPanel } from './components/Rides/MyRidesPanel';
import { TourDetailModal } from './components/Tours/TourDetailModal';
import { RouteDetailModal } from './components/Community/RouteDetailModal';
import { CategoryPage } from './components/Category/CategoryPage';
import { UserProfilePanel } from './components/Profile/UserProfilePanel';
import { EmbedCard } from './components/Embed/EmbedCard';
import { CommunityRouteInfo } from './components/CommunityRouteInfo/CommunityRouteInfo';
import { OnboardingModal } from './components/Onboarding/OnboardingModal';
import { ComingSoon } from './components/ComingSoon/ComingSoon';
import { Toast } from './components/common/Toast';
import { BookingStatusModal } from './components/Tours/BookingStatusModal';
import { useRoute } from './context/RouteContext';
import { useAuth } from './context/AuthContext';
import { useRouting } from './hooks/useRouting';
import { syncSubscription, confirmTicket } from './services/stripeService';
import { track, REVENUE_CURRENCY } from './services/analytics';

// Revenue Motoroam earns per subscriber, by plan (matches the pricing UI).
const SUBSCRIPTION_VALUE = { monthly: 1.99, yearly: 9.99 };
import { fetchTour } from './services/tourService';
import { fetchPublishedRoute } from './services/publishService';
import './styles/global.css';
import './App.css';

function AppInner() {
  useRouting();
  const { state, showToast } = useRoute();
  const { user, profile, profileLoading, loading, refreshSubscription } = useAuth();
  const subHandled = useRef(false);
  const deepHandled = useRef(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showRides, setShowRides] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [showBusinessApply, setShowBusinessApply] = useState(false);
  const [deepTour, setDeepTour] = useState(null);
  const [deepRoute, setDeepRoute] = useState(null);
  const [deepBuy, setDeepBuy] = useState(false);
  const [category, setCategory] = useState(null);
  const [gearUid, setGearUid] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showTicketConfirmed, setShowTicketConfirmed] = useState(false);

  // "Sign up as a business" (AuthBadge, signed-out) sets a one-shot intent
  // flag before the user picks Google/email-link — once auth completes, open
  // the application modal directly rather than assuming they'll find Profile.
  useEffect(() => {
    if (!user || profileLoading) return;
    let intent = null;
    try { intent = localStorage.getItem(BUSINESS_SIGNUP_INTENT_KEY); } catch { /* ignore */ }
    if (!intent) return;
    try { localStorage.removeItem(BUSINESS_SIGNUP_INTENT_KEY); } catch { /* ignore */ }
    const status = profile?.businessVerifiedStatus;
    if (status === 'verified') showToast('Your business is already verified');
    else if (status === 'pending') showToast('Your business application is already under review');
    else setShowBusinessApply(true);
  }, [user, profile, profileLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // First-visit onboarding: show once to signed-out visitors (not on deep links).
  useEffect(() => {
    if (loading || user) { setShowOnboarding(false); return; }
    const isDeepLink = window.location.pathname !== '/' || window.location.search;
    let seen = false;
    try { seen = !!localStorage.getItem('motoroam-onboarded'); } catch { /* ignore */ }
    if (!seen && !isDeepLink) setShowOnboarding(true);
  }, [loading, user]);

  const dismissOnboarding = () => {
    setShowOnboarding(false);
    try { localStorage.setItem('motoroam-onboarded', '1'); } catch { /* ignore */ }
  };

  // Loading a route minimizes the UI so the map is visible — close every page.
  useEffect(() => {
    if (state.mapFocus === 0) return;
    setShowProfile(false);
    setShowRides(false);
    setCategory(null);
    setDeepTour(null);
    setDeepRoute(null);
    setGearUid(null);
  }, [state.mapFocus]);

  // Deep links:
  //   /tour/<id> (or ?tour=<id>)  → open the tour; &buy=1 jumps to checkout
  //   /route/<id>                 → open the published route
  useEffect(() => {
    if (loading || deepHandled.current) return;
    const params = new URLSearchParams(window.location.search);
    const path = window.location.pathname;

    // Shared gearlist → open the rider's public profile
    const gid = params.get('gear');
    if (gid) {
      deepHandled.current = true;
      setGearUid(gid);
      return;
    }

    // Category / SEO landing pages
    if (/^\/curvy-roads\/?$/.test(path)) {
      deepHandled.current = true;
      setCategory({ kind: 'curvy', title: 'Curviest Motorcycle Roads' });
      return;
    }
    const countrySlug = path.match(/^\/(?:routes|tours)\/([a-z0-9-]+)\/?$/)?.[1];
    if (countrySlug) {
      deepHandled.current = true;
      const name = countrySlug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      setCategory({ kind: 'country', slug: countrySlug, title: `Motorcycle Routes & Tours in ${name}` });
      return;
    }

    const tourId  = path.match(/^\/tour\/([A-Za-z0-9_-]+)/)?.[1]  || params.get('tour');
    const routeId = path.match(/^\/route\/([A-Za-z0-9_-]+)/)?.[1];
    if (!tourId && !routeId) return;
    deepHandled.current = true;

    if (tourId) {
      setDeepBuy(params.get('buy') === '1');
      fetchTour(tourId)
        .then(t => { t ? setDeepTour(t) : showToast('Tour not found.', 'error'); })
        .catch(() => showToast('Could not load that tour.', 'error'));
    } else if (routeId) {
      fetchPublishedRoute(routeId)
        .then(r => { r ? setDeepRoute(r) : showToast('Route not found.', 'error'); })
        .catch(() => showToast('Could not load that route.', 'error'));
    }
  }, [loading, showToast]);

  // Handle all Stripe redirect query params — wait for auth to finish loading first
  useEffect(() => {
    if (loading || subHandled.current) return;
    const params = new URLSearchParams(window.location.search);
    const sub         = params.get('sub');
    const ticketTourId = params.get('ticket_success');
    const connectResult = params.get('connect');

    if (sub === 'success') {
      subHandled.current = true;
      window.history.replaceState(null, '', window.location.pathname);
      showToast('Subscription activated! Welcome to Motoroam Pro.', 'success');
      if (user) {
        syncSubscription()
          .then(data => {
            refreshSubscription();
            // Revenue Motoroam earns from this subscriber.
            const value = SUBSCRIPTION_VALUE[data?.plan] ?? null;
            if (value != null) {
              track('new_subscriber', { value, currency: REVENUE_CURRENCY, plan: data.plan });
            }
          })
          .catch(err => console.warn('Subscription sync failed:', err));
      }
    } else if (sub === 'cancel') {
      subHandled.current = true;
      window.history.replaceState(null, '', window.location.pathname);
    } else if (ticketTourId) {
      subHandled.current = true;
      const sessionId = params.get('session_id');
      window.history.replaceState(null, '', window.location.pathname);
      setShowTicketConfirmed(true);
      // Belt-and-braces: ensure the registration exists even if the Connect
      // webhook is delayed or undelivered. Also logs the booking-fee revenue.
      if (user && sessionId) {
        confirmTicket(ticketTourId, sessionId)
          .then(data => {
            if (data?.confirmed && data.value != null) {
              track('ticket_purchase', {
                value: data.value,                 // Motoroam's booking fee
                currency: REVENUE_CURRENCY,
                ticket_price: data.ticketPrice ?? null,
                tour_id: ticketTourId,
              });
            }
          })
          .catch(err => console.warn('Ticket confirm failed:', err));
      }
    } else if (connectResult === 'refresh') {
      // Onboarding link expired — open Profile so user can restart
      subHandled.current = true;
      window.history.replaceState(null, '', window.location.pathname);
      showToast('Stripe onboarding link expired. Please try again.', 'info');
      setShowProfile(true);
    } else if (params.get('settings') === 'notifications') {
      // "Manage email preferences" link in email footers.
      subHandled.current = true;
      window.history.replaceState(null, '', window.location.pathname);
      setShowProfile(true);
    }
  }, [loading, user, showToast, refreshSubscription]); // eslint-disable-line

  return (
    <div className="app">
      <MapView />
      <Sidebar
        onOpenProfile={() => setShowProfile(true)}
        onOpenCategory={(cat) => {
          window.history.pushState(null, '', cat.kind === 'curvy' ? '/curvy-roads' : `/routes/${cat.slug}`);
          setCategory(cat);
        }}
      />
      <AuthBadge
        onOpenProfile={() => setShowProfile(true)}
        onOpenRides={() => setShowRides(true)}
        onOpenAdmin={() => setShowAdmin(true)}
        onOpenDashboard={() => setShowDashboard(true)}
      />
      <CommunityRouteInfo />
      <Toast toast={state.toast} />
      {showOnboarding && <OnboardingModal onClose={dismissOnboarding} />}
      {showProfile && <ProfilePage onClose={() => setShowProfile(false)} />}
      {showRides && <MyRidesPanel onClose={() => setShowRides(false)} />}
      {showAdmin && <AdminPanel onClose={() => setShowAdmin(false)} />}
      {showDashboard && (
        <BusinessDashboard
          onClose={() => setShowDashboard(false)}
          onOpenProfile={() => { setShowDashboard(false); setShowProfile(true); }}
        />
      )}
      {showBusinessApply && user && (
        <BusinessApplicationModal
          uid={user.uid}
          initialData={profile || {}}
          onClose={() => setShowBusinessApply(false)}
          onSubmitted={() => showToast('Application submitted')}
        />
      )}
      {showTicketConfirmed && (
        <BookingStatusModal
          title="You're registered!"
          subtitle="Check your email for confirmation."
          onClose={() => setShowTicketConfirmed(false)}
        />
      )}
      {deepTour && (
        <TourDetailModal
          tour={deepTour}
          autoBuy={deepBuy}
          onClose={() => { setDeepTour(null); window.history.replaceState(null, '', '/'); }}
          onUpgrade={() => { setDeepTour(null); setShowProfile(true); }}
        />
      )}
      {deepRoute && (
        <RouteDetailModal
          route={deepRoute}
          onClose={() => { setDeepRoute(null); window.history.replaceState(null, '', '/'); }}
          onUnpublished={() => { setDeepRoute(null); window.history.replaceState(null, '', '/'); }}
          onExploreUser={() => { setDeepRoute(null); window.history.replaceState(null, '', '/'); }}
        />
      )}
      {gearUid && (
        <UserProfilePanel
          uid={gearUid}
          zIndex={1600}
          onClose={() => { setGearUid(null); window.history.replaceState(null, '', '/'); }}
        />
      )}
      {category && (
        <CategoryPage
          kind={category.kind}
          slug={category.slug}
          title={category.title}
          onClose={() => { setCategory(null); window.history.replaceState(null, '', '/'); }}
          onOpenRoute={(r) => setDeepRoute(r)}
          onOpenTour={(t) => setDeepTour(t)}
        />
      )}
    </div>
  );
}

// Pre-launch gate: show "Coming soon" on the LIVE domain, but let preview
// channels (hostname has "--"), localhost, and ?preview=1 reach the real app.
function shouldShowComingSoon() {
  const h = window.location.hostname;
  if (h === 'localhost' || h === '127.0.0.1' || h.includes('--')) return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get('preview') === '1') {
    try { localStorage.setItem('mr-preview', '1'); } catch { /* ignore */ }
    return false;
  }
  try { if (localStorage.getItem('mr-preview') === '1') return false; } catch { /* ignore */ }
  return true;
}

export default function App() {
  // Standalone embed view — no app chrome, no providers needed.
  const embed = window.location.pathname.match(/^\/embed\/(route|tour)\/([A-Za-z0-9_-]+)/);
  if (embed) return <EmbedCard kind={embed[1]} id={embed[2]} />;

  if (shouldShowComingSoon()) return <ComingSoon />;

  return (
    <AuthProvider>
      <RouteProvider>
        <AppInner />
      </RouteProvider>
    </AuthProvider>
  );
}
